import { countSegments, GSM_REPLACEMENTS, type SegmentInfo, toGsmSafe } from './segments.js';

/**
 * Compose-box rules on top of the segment counter (`segments.ts` stays the only counter).
 * The worker applies the same cap before sending, so a text the box refuses can never go out.
 */

/** Longest manual text, counted with the real encoding (GSM-7: 1,530 characters; UCS-2: 670). */
export const MAX_SEGMENTS = 10;

/**
 * The text as it will be sent: the opt-out footer on its own line when the database says it applies
 * (`message_footer` RPC), unless the text already ends with it. Mirrors `public.send_message`.
 */
export function withFooter(body: string, footer: string | null): string {
  if (!footer) return body;
  if (body.toLowerCase().endsWith(footer.toLowerCase())) return body;
  return `${body}\n${footer}`;
}

export interface LookAlikeChange {
  /** The character as typed (e.g. a curly quote). */
  from: string;
  /** Its plain GSM-7 replacement ('' when it is removed, like a zero-width space). */
  to: string;
  count: number;
  /** A readable name for characters that are hard to see. */
  name: string;
}

const NAMES: Readonly<Record<string, string>> = {
  '‘': 'left single quote',
  '’': 'right single quote',
  '‚': 'low single quote',
  '‛': 'reversed single quote',
  '′': 'prime',
  '`': 'backtick',
  '´': 'acute accent',
  '‹': 'single angle quote',
  '›': 'single angle quote',
  '“': 'left double quote',
  '”': 'right double quote',
  '„': 'low double quote',
  '‟': 'reversed double quote',
  '″': 'double prime',
  '«': 'left guillemet',
  '»': 'right guillemet',
  '‐': 'hyphen',
  '‑': 'non-breaking hyphen',
  '‒': 'figure dash',
  '–': 'en dash',
  '—': 'em dash',
  '―': 'horizontal bar',
  '−': 'minus sign',
  '…': 'ellipsis',
  '•': 'bullet',
  '·': 'middle dot',
  ' ': 'no-break space',
  ' ': 'en space',
  ' ': 'em space',
  ' ': 'thin space',
  ' ': 'narrow no-break space',
  '\u0009': 'tab',
  '​': 'zero-width space',
  '‌': 'zero-width non-joiner',
  '‍': 'zero-width joiner',
  '﻿': 'byte order mark',
};

/**
 * What "Replace look-alikes" would change, before it changes anything: each look-alike character, its
 * replacement and how often it occurs, in order of first appearance.
 */
export function lookAlikeChanges(text: string): LookAlikeChange[] {
  const changes = new Map<string, LookAlikeChange>();
  for (const char of text) {
    const to = GSM_REPLACEMENTS[char];
    if (to === undefined) continue;
    const existing = changes.get(char);
    if (existing) existing.count += 1;
    else
      changes.set(char, { from: char, to, count: 1, name: NAMES[char] ?? 'look-alike character' });
  }
  return [...changes.values()];
}

export interface ComposeCheck {
  /** The text that will be sent (footer included). */
  finalText: string;
  info: SegmentInfo;
  /** Over the 10-segment cap: the box refuses to send. */
  overCap: boolean;
  /** More than one segment: the sender must confirm before it goes. */
  needsConfirmation: boolean;
  /** Characters that force UCS-2 (emoji, curly quotes, em dashes, …). */
  costlyCharacters: string[];
  /** Look-alikes that can be replaced, and whether replacing them saves segments. */
  lookAlikes: LookAlikeChange[];
  replacementSavesSegments: boolean;
  /** Segments after replacing look-alikes (same as info.segments when nothing changes). */
  segmentsAfterReplacement: number;
}

/** Everything the compose box shows about a draft. `body` is the trimmed draft. */
export function checkCompose(body: string, footer: string | null): ComposeCheck {
  const finalText = body.length === 0 ? '' : withFooter(body, footer);
  const info = countSegments(finalText);
  const replaced = toGsmSafe(finalText);
  const after = replaced.replaced > 0 ? countSegments(replaced.text).segments : info.segments;
  return {
    finalText,
    info,
    overCap: info.segments > MAX_SEGMENTS,
    needsConfirmation: info.segments > 1,
    costlyCharacters: info.nonGsmCharacters,
    lookAlikes: lookAlikeChanges(body),
    replacementSavesSegments: after < info.segments,
    segmentsAfterReplacement: after,
  };
}

/** Longest text that still fits in MAX_SEGMENTS with this encoding, for the cap hint. */
export function maxCharacters(encoding: SegmentInfo['encoding']): number {
  return encoding === 'gsm7' ? 153 * MAX_SEGMENTS : 67 * MAX_SEGMENTS;
}
